<template>
  <article>
    <custom-callout
      class="mt-3"
      :has-toggle="false"
      :border-color="borderColor"
    >
      <div class="row pt-2">
        <div class="col-6 col-md-2">
          <div class="info-text-title">
            Número de SIE
          </div>
          <p class="info-text">
            {{ value.studentSieId }}
          </p>
        </div>
        <div class="col-6 col-md-2">
          <div class="info-text-title">
            Nombre del estudiante
          </div>
          <p class="info-text">
            {{ value.fullName }}
          </p>
        </div>
        <div
          :class="[
            'col-6',
            { 'col-md-2': !showActions, 'col-md-1': showActions },
          ]"
        >
          <div class="info-text-title">
            Edad:
          </div>
          <p class="info-text">
            {{ value.age }}
          </p>
        </div>
        <div
          :class="[
            'col-6',
            { 'col-md-2': !showActions, 'col-md-1': showActions },
          ]"
        >
          <div class="info-text-title">
            Grado:
          </div>
          <p class="info-text">
            {{ value.grade.name }}
          </p>
        </div>
        <div class="col-6 col-md-2">
          <div class="info-text-title">
            Impedimento:
          </div>
          <p class="info-text">
            {{ value.impediment.name }}
          </p>
        </div>
        <div class="col-6 col-md-2">
          <div class="info-text-title">
            Estatus:
          </div>
          <p class="info-text">
            {{ value.status.name }}
          </p>
        </div>
        <div
          v-if="showActions"
          class="col-12 col-md-2 d-md-flex justify-content-center"
        >
          <div class="align-self-center">
            <ActionButton
              v-if="showRemoveAction"
              text="Remover"
              variant="outline-danger"
              i="far fa-minus-circle"
              :disabled="isLoading"
              :responsive="true"
              @click="onRemove"
            />
            <ActionButton
              v-if="showAssignAction"
              text="Asignar"
              variant="outline-success"
              i="fas fa-user-plus"
              :responsive="true"
              :disabled="isLoading"
              @click="onAssign"
            />
          </div>
        </div>
      </div>
    </custom-callout>
  </article>
</template>

<script>
import CustomCallout from 'custom-callout';
import ActionButton from 'ActionButton';
import StudentStatuses from '@/utils/constants/student/studentStatuses';
import {
  unassignStudentToTeacher,
  assignStudentToTeacher,
} from '@/modules/alternative-location-management/api';
import alertMixin from '@/mixin/alert.mixin';
import loadingMixin from '@/mixin/loading.mixin';

export default {
  name: 'StudentResultRow',
  mixins: [alertMixin, loadingMixin],
  components: {
    CustomCallout,
    ActionButton,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    collections: {
      type: Object,
      required: true,
    },
    teacherId: {
      type: Number,
      default: 0,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    showRemoveAction: {
      type: Boolean,
      default: false,
    },
    showAssignAction: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    schoolAlternativeLocationId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
    };
  },
  computed: {
    borderColor() {
      return this.value.recentlyAdded ? 'green' : 'gray';
    },
  },
  methods: {
    async onRemove() {
      const response = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea remover el estudiante ${this.value.fullName}?`
      );

      if (!response) return;

      this.isLoading = true;

      unassignStudentToTeacher({
        assignToId: this.value.teacherId,
        studentSieId: Number(this.value.studentSieId),
        schoolAlternativeLocationId: this.schoolAlternativeLocationId,
      })
        .then((res) => {
          this.isLoading = false;

          if (res.status == 200) {
            this.$emit('on-removed', this.currentPage);
            this.ShowToast(
              'Confirmación',
              this.collections.resources['R01210013'],
              'success'
            );
          }
        })
        .catch((err) => {
          this.isLoading = false;

          if (err && err.response && err.response.data) {
            this.ShowToast('Error', err.response.data.message, 'error');
          } else {
            this.ShowToast('Error', err.message, 'error');
          }
        });
    },
    async onAssign() {
      const allowedStudentStatuses = [
        StudentStatuses.Elegible,
        StudentStatuses.Servido,
      ];
      if (!allowedStudentStatuses.includes(this.value.status.id)) {
        this.ShowToast(
          'Alerta',
          this.collections.resources['R01210005'],
          'error'
        );
        return;
      }

      this.isLoading = true;

      assignStudentToTeacher({
        assignToId: this.teacherId,
        studentSieId: Number(this.value.studentSieId),
        schoolAlternativeLocationId: this.schoolAlternativeLocationId,
      })
        .then((res) => {
          this.isLoading = false;
          if (res.status == 200) {
            this.$emit('on-assigned', this.currentPage);
            this.ShowToast(
              'Confirmación',
              this.collections.resources['R01210008'],
              'success'
            );
          }
        })
        .catch((err) => {
          this.isLoading = false;
          if (err && err.response && err.response.data) {
            this.ShowToast('Error', err.response.data.message, 'error');
          } else {
            this.ShowToast('Error', err.message, 'error');
          }
        });
    },
  },
  watch: {
    isLoading(value) {
      if (value) {
        this.ShowWait();
      } else {
        this.HideWait();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@import '@/assets/scss/variables/_colors.scss';

.info-text-title {
  color: map.get($colors, 'gray-6');
  font-size: 1rem;
  font-weight: normal;
}

.info-text {
  color: map.get($colors, 'black');
  font-weight: normal;
  font-size: 1rem;
  size: 1rem;
}
</style>
